import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { formatAssemblyDate, normalizeText } from './assemblyScheduleConfig';

const getExportValue = (job, column) => {
  const value = job?.[column.key];
  return column.type === 'date' ? formatAssemblyDate(value) : normalizeText(value);
};

const buildRows = ({ columns, sortedWeeks, groupedJobs, getWeekLabel }) => {
  const rows = [];
  sortedWeeks.forEach((week) => {
    const jobs = groupedJobs[week] || [];
    if (jobs.length === 0) return;
    rows.push({ type: 'week', values: [getWeekLabel(week), ...columns.slice(1).map(() => '')] });
    jobs.forEach((job) => {
      rows.push({ type: 'job', values: columns.map((column) => getExportValue(job, column)) });
    });
  });
  return rows;
};

const getFilenameDate = () => new Date().toISOString().slice(0, 10);

export const exportAssemblyScheduleToExcel = (context) => {
  const { columns } = context;
  const rows = buildRows(context);
  const values = [columns.map((column) => column.label), ...rows.map((row) => row.values)];
  const worksheet = XLSX.utils.aoa_to_sheet(values);
  worksheet['!cols'] = columns.map((column, columnIndex) => ({
    wch: Math.min(45, Math.max(
      column.label.length + 2,
      ...rows.filter((row) => row.type === 'job').map((row) => String(row.values[columnIndex] || '').length + 2)
    )),
  }));
  worksheet['!merges'] = rows.reduce((merges, row, index) => {
    if (row.type === 'week') {
      merges.push({ s: { r: index + 1, c: 0 }, e: { r: index + 1, c: columns.length - 1 } });
    }
    return merges;
  }, []);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Assembly Schedule');
  XLSX.writeFile(workbook, `Watertronics_Assembly_Schedule_${getFilenameDate()}.xlsx`);
};

export const exportAssemblyScheduleToPdf = (context) => {
  const { columns } = context;
  const rows = buildRows(context);
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: [792, 1224] });
  pdf.setFontSize(16);
  pdf.setTextColor(0, 85, 165);
  pdf.text('Watertronics Assembly Schedule', 28, 30);
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`Generated ${new Date().toLocaleString()}`, 28, 45);
  pdf.autoTable({
    head: [columns.map((column) => column.label)],
    body: rows.map((row) => row.values),
    startY: 55,
    theme: 'grid',
    styles: { fontSize: 6.5, cellPadding: 2, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: [0, 85, 165], textColor: 255, fontStyle: 'bold' },
    didParseCell: (data) => {
      const sourceRow = rows[data.row.index];
      if (data.section === 'body' && sourceRow?.type === 'week') {
        if (data.column.index === 0) data.cell.colSpan = columns.length;
        data.cell.styles.fillColor = [146, 208, 80];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = [18, 33, 11];
      }
    },
    margin: { top: 55, right: 24, bottom: 24, left: 24 },
  });
  pdf.save(`Watertronics_Assembly_Schedule_${getFilenameDate()}.pdf`);
};

const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

export const printAssemblySchedule = (context) => {
  const { columns } = context;
  const rows = buildRows(context);
  const printWindow = window.open('', '_blank');
  if (!printWindow) throw new Error('The print window was blocked by the browser.');
  printWindow.opener = null;

  const tableRows = rows.map((row) => row.type === 'week'
    ? `<tr class="week"><td colspan="${columns.length}">${escapeHtml(row.values[0])}</td></tr>`
    : `<tr>${row.values.map((value) => `<td>${escapeHtml(value)}</td>`).join('')}</tr>`
  ).join('');
  printWindow.document.write(`<!doctype html><html><head><title>Assembly Schedule</title><style>
    @page { size: 17in 11in landscape; margin: 0.35in; }
    body { font-family: Arial, sans-serif; margin: 0; }
    h1 { color: #0055a5; font-size: 18px; margin: 0 0 4px; }
    p { font-size: 10px; margin: 0 0 10px; }
    table { border-collapse: collapse; font-size: 7px; width: 100%; }
    th, td { border: 1px solid #adb5bd; padding: 3px; text-align: left; vertical-align: top; }
    th { background: #0055a5; color: white; }
    tr { break-inside: avoid; }
    .week td { background: #92d050; color: #12210b; font-size: 9px; font-weight: bold; }
  </style></head><body><h1>Watertronics Assembly Schedule</h1>
  <p>Generated ${escapeHtml(new Date().toLocaleString())}</p><table><thead><tr>
  ${columns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('')}
  </tr></thead><tbody>${tableRows}</tbody></table></body></html>`);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};
